import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Ride, RideStatus, CreateRideInput, UpdateRideStatusInput, RideSearchInput } from '@ouagaliv/shared';
import { api } from '../lib/api';

interface RideContextType {
  currentRide: Ride | null;
  rides: Ride[];
  isLoading: boolean;
  createRide: (data: CreateRideInput) => Promise<Ride>;
  getRide: (rideId: string) => Promise<Ride>;
  searchRides: (params: RideSearchInput) => Promise<{ items: Ride[]; total: number }>;
  acceptRide: (rideId: string) => Promise<Ride>;
  updateRideStatus: (rideId: string, data: UpdateRideStatusInput) => Promise<Ride>;
  clearCurrentRide: () => void;
  setCurrentRide: (ride: Ride | null) => void;
}

const RideContext = createContext<RideContextType | undefined>(undefined);

export function RideProvider({ children }: { children: ReactNode }) {
  const [currentRide, setCurrentRide] = useState<Ride | null>(null);
  const [rides, setRides] = useState<Ride[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const createRide = async (data: CreateRideInput): Promise<Ride> => {
    setIsLoading(true);
    try {
      const res = await api.post<{ success: boolean; data: Ride }>('/rides', data);
      if (!res.data.success) throw new Error('Failed to create ride');
      return res.data.data;
    } finally {
      setIsLoading(false);
    }
  };

  const getRide = async (rideId: string): Promise<Ride> => {
    const res = await api.get<{ success: boolean; data: Ride }>(`/rides/${rideId}`);
    if (!res.data.success) throw new Error('Failed to get ride');
    const ride = res.data.data;
    setCurrentRide(ride);
    return ride;
  };

  const searchRides = async (params: RideSearchInput): Promise<{ items: Ride[]; total: number }> => {
    setIsLoading(true);
    try {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) searchParams.append(key, String(value));
      });
      const res = await api.get<{ success: boolean; data: { items: Ride[]; total: number } }>(`/rides/search?${searchParams}`);
      if (!res.data.success) throw new Error('Failed to search rides');
      setRides(res.data.data.items);
      return res.data.data;
    } finally {
      setIsLoading(false);
    }
  };

  const acceptRide = async (rideId: string): Promise<Ride> => {
    const res = await api.post<{ success: boolean; data: Ride }>(`/rides/${rideId}/accept`);
    if (!res.data.success) throw new Error('Failed to accept ride');
    const ride = res.data.data;
    setCurrentRide(ride);
    return ride;
  };

  const updateRideStatus = async (rideId: string, data: UpdateRideStatusInput): Promise<Ride> => {
    const res = await api.patch<{ success: boolean; data: Ride }>(`/rides/${rideId}/status`, data);
    if (!res.data.success) throw new Error('Failed to update ride status');
    const ride = res.data.data;
    setCurrentRide(ride);
    return ride;
  };

  const clearCurrentRide = () => setCurrentRide(null);

  return (
    <RideContext.Provider value={{ currentRide, rides, isLoading, createRide, getRide, searchRides, acceptRide, updateRideStatus, clearCurrentRide, setCurrentRide }}>
      {children}
    </RideContext.Provider>
  );
}

export function useRide() {
  const context = useContext(RideContext);
  if (!context) throw new Error('useRide must be used within RideProvider');
  return context;
}